import type { CreateCatalogRequest, ItemCopyDetails } from "@/lib/api/catalog";

export type JournalFields = NonNullable<CreateCatalogRequest["journal"]>;

export interface JournalHoldingEntry {
  key: string;
  journal: JournalFields;
  copies: number;
  itemCopies: ItemCopyDetails[];
}

export interface JournalCatalogState {
  biblio: CreateCatalogRequest["biblio"];
  holdings: JournalHoldingEntry[];
  itemDefaults: CreateCatalogRequest["itemDefaults"];
}

let holdingKey = 0;
export function createHoldingKey() {
  holdingKey += 1;
  return `holding-${holdingKey}`;
}

export function createEmptyHolding(): JournalHoldingEntry {
  return {
    key: createHoldingKey(),
    journal: { volumeNumber: "", volumeOnly: false },
    copies: 1,
    itemCopies: [{}],
  };
}

export const EMPTY_JOURNAL_STATE: JournalCatalogState = {
  biblio: {
    title: "",
    subtitle: "",
    authors: [],
    editors: [],
    issn: "",
    eIssn: "",
    issnL: "",
    doi: "",
    publisher: "",
    placeOfPublication: "",
    pages: undefined,
    subjects: [],
    description: "",
    websiteUrl: "",
    lcClassification: "",
    callNumber: "",
    bindingType: "journal",
  },
  holdings: [createEmptyHolding()],
  itemDefaults: { statusCode: "AVAILABLE" },
};

const normalizeIssn = (value?: string) =>
  value?.replace(/[-\s]/g, "").trim().toUpperCase() || undefined;
const optionalText = (value?: string) => value?.trim() || undefined;

function normalizeJournalFields(journal: JournalFields): JournalFields {
  return {
    ...journal,
    volumeNumber: journal.volumeNumber.trim(),
    volumePublicationYear: optionalText(journal.volumePublicationYear),
    issueNumberFrom: journal.volumeOnly ? undefined : journal.issueNumberFrom,
    issueNumberTo: journal.volumeOnly ? undefined : journal.issueNumberTo,
    issuePublicationYear: journal.volumeOnly
      ? undefined
      : optionalText(journal.issuePublicationYear),
    issuePublicationDate: journal.volumeOnly
      ? undefined
      : optionalText(journal.issuePublicationDate),
  };
}

export function buildJournalPayload(state: JournalCatalogState): CreateCatalogRequest {
  const {
    publishedDate: _publishedDate,
    seriesInformation: _seriesInformation,
    pages: _pages,
    ...biblio
  } = state.biblio;
  const first = state.holdings[0];
  const journalHoldings = state.holdings.map((holding) => ({
    ...normalizeJournalFields(holding.journal),
    copies: holding.copies,
    itemCopies: holding.itemCopies,
  }));
  return {
    catalogMethod: "JOURNAL",
    biblio: {
      ...biblio,
      subtitle: optionalText(biblio.subtitle),
      edition: optionalText(biblio.edition),
      doi: optionalText(biblio.doi),
      publisher: optionalText(biblio.publisher),
      placeOfPublication: optionalText(biblio.placeOfPublication),
      illustrations: optionalText(biblio.illustrations),
      size: optionalText(biblio.size),
      description: optionalText(biblio.description),
      coverUrl: optionalText(biblio.coverUrl),
      websiteUrl: optionalText(biblio.websiteUrl),
      lcClassification: optionalText(biblio.lcClassification),
      callNumber: optionalText(biblio.callNumber),
      bindingType: optionalText(biblio.bindingType),
      issn: normalizeIssn(biblio.issn),
      eIssn: normalizeIssn(biblio.eIssn),
      issnL: normalizeIssn(biblio.issnL),
    },
    journal: first ? normalizeJournalFields(first.journal) : { volumeNumber: "" },
    journalHoldings,
    copies: first?.copies ?? 1,
    itemDefaults: {
      ...state.itemDefaults,
      itemTypeId: state.itemDefaults.itemTypeId || undefined,
      locationId: state.itemDefaults.locationId || undefined,
      collectionId: state.itemDefaults.collectionId || undefined,
      branchId: state.itemDefaults.branchId || undefined,
      conditionCode: state.itemDefaults.conditionCode || undefined,
      statusCode: state.itemDefaults.statusCode || "AVAILABLE",
      acquisitionMode: state.itemDefaults.acquisitionMode || undefined,
    },
    itemCopies: first?.itemCopies ?? [{}],
  };
}

export function stateFromDraft(payload: CreateCatalogRequest): JournalCatalogState {
  const holdings = payload.journalHoldings?.length
    ? payload.journalHoldings.map((holding) => ({
        key: createHoldingKey(),
        journal: {
          volumeNumber: holding.volumeNumber,
          volumePublicationYear: holding.volumePublicationYear,
          issueNumberFrom: holding.issueNumberFrom,
          issueNumberTo: holding.issueNumberTo,
          issuePublicationYear: holding.issuePublicationYear,
          issuePublicationDate: holding.issuePublicationDate,
          volumeOnly: holding.volumeOnly ?? false,
        },
        copies: holding.copies,
        itemCopies: holding.itemCopies?.length ? holding.itemCopies : [{}],
      }))
    : [
        {
          key: createHoldingKey(),
          journal: { ...EMPTY_JOURNAL_STATE.holdings[0].journal, ...payload.journal },
          copies: payload.copies,
          itemCopies: payload.itemCopies?.length ? payload.itemCopies : [{}],
        },
      ];
  return {
    biblio: { ...EMPTY_JOURNAL_STATE.biblio, ...payload.biblio },
    holdings,
    itemDefaults: { ...EMPTY_JOURNAL_STATE.itemDefaults, ...payload.itemDefaults },
  };
}
